import { ActionContext, Module } from 'vuex';
import _ from 'lodash';
import api from '@/services/Api';
import moment from 'moment';
import { AxiosError, AxiosResponse } from 'axios';
import { errorSwal, successSwal, warningSwal } from '@/helper/swals';
import { saveAs } from 'file-saver';
import {
    ElgaUebertagungFilter,
    ElgauebertragungEintrag,
    ElgaUebertragungState, RootState,
    UebertragungStatus,
} from '@/store/types';

export const defaultFilter: ElgaUebertagungFilter = {
    zeitraum: [
        moment().subtract(1, 'week').format('YYYY-MM-DD'),
        moment().format('YYYY-MM-DD'),
    ],
    status: UebertragungStatus.NichtUebertragen,
    patientStatus: null,
};

export type SetElgaUebertragungLoaders = {
    impfungen?: boolean;
    uebertragung?: boolean;
    stornierung?: boolean;
    suche?: boolean;
    export?: boolean
};

export type UebertragenImpfungenPayload = {
    patienten: number[];
};

export type StornierenImpfungenPayload = {
    patienten: number[];
};

export type SuchenImpfungenPayload = {
    patienten: number[];
};

export type PruefenImpfungenPayload = {
    patienten: number[];
};

type Context = ActionContext<ElgaUebertragungState, RootState>;

export const elgauebertragung: Module<ElgaUebertragungState, RootState> = {
    namespaced: true,
    state: {
        loaders: {
            impfungen: false,
            uebertragung: false,
            stornierung: false,
            suche: false,
            export: false,
            verfication: false,
        },
        items: [],
        selectedItems: [],
        patienten: [],
        page: 1,
        prevFilter: {
            ...defaultFilter,
        },
        filter: {
            ...defaultFilter,
        }
    },
    actions: {
        loadImpfungen({ commit, state }: Context, payload: ElgaUebertagungFilter): void {
            commit('setLoaders', { impfungen: true });

            const filter = {
                ...state.filter,
                ...payload,
            };

            commit('setFilter', filter);

            api.post('verwaltung/elgauebertragung', { filter })
                .then((response: AxiosResponse) => {
                    commit('setItems', response.data.body);

                    if (payload) {
                        commit('setPrevFilter', payload);
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { impfungen: false }));
        },
        uebertragenImpfungen({ commit, dispatch, state, rootState }: Context,
                             payload: UebertragenImpfungenPayload): void {
            commit('setLoaders', { uebertragung: true });

            const { zeitraum } = state.prevFilter;
            const arbeitsplatz_id = rootState.session.arbeitsplatz;
            const { impfstelle_id } = rootState.authentication;

            api.post('ivs/impfpass/syncall', { ...payload, zeitraum, arbeitsplatz_id, impfstelle_id })
                .then((response: AxiosResponse) => {
                    const body = response.data?.body;

                    if (typeof body === 'string') {
                        errorSwal('Fehler', body);
                    } else {
                        const results = Object.values(body || {}) as any[];
                        const allFailed = results.length > 0 &&
                            results.every((r: any) => !r.found);

                        if (allFailed) {
                            const errors = results.map((r: any) => r.errors).filter(Boolean);
                            warningSwal('ELGA', errors.join(', ') || 'Keine Impfungen übertragen');
                        } else {
                            successSwal('ELGA', 'übertragen');
                        }
                    }

                    dispatch('loadImpfungen');
                })
                .catch((error: AxiosError<{ body?: any }>) => {
                    console.error(error);
                    errorSwal('Fehler', error.response?.data?.body ?? error.message);
                })
                .finally(() => commit('setLoaders', { uebertragung: false }));
        },
        stornierenImpfungen({ commit, dispatch, state, rootState }: Context,
                            payload: StornierenImpfungenPayload): void {
            commit('setLoaders', { stornierung: true });

            const { zeitraum } = state.prevFilter;
            const arbeitsplatz_id = rootState.session.arbeitsplatz;
            const { impfstelle_id } = rootState.authentication;

            api.post('ivs/impfpass/cancelall', { ...payload, zeitraum, arbeitsplatz_id, impfstelle_id })
                .then((response: AxiosResponse) => {
                    const body = response.data?.body;

                    if (typeof body === 'string') {
                        errorSwal('Fehler', body);
                    } else {
                        const results = Object.values(body || {}) as any[];
                        const allFailed = results.length > 0 &&
                            results.every((r: any) => !r.found);

                        if (allFailed) {
                            const errors = results.map((r: any) => r.errors).filter(Boolean);
                            warningSwal('ELGA', errors.join(', ') || 'Keine Stornierungen durchgeführt');
                        } else {
                            successSwal('ELGA', 'storniert');
                        }
                    }

                    dispatch('loadImpfungen');
                })
                .catch((error: AxiosError<{ body?: any }>) => {
                    console.error(error);
                    errorSwal('Fehler', error.response?.data?.body ?? error.message);
                })
                .finally(() => commit('setLoaders', { stornierung: false }));
        },
        suchenPatienten({ commit, dispatch, rootState }: Context, payload: SuchenImpfungenPayload): void {
            commit('setLoaders', { suche: true });

            const arbeitsplatz_id = rootState.session.arbeitsplatz;
            const { impfstelle_id } = rootState.authentication;

            api.post('ivs/impfpass/searchall', { ...payload, arbeitsplatz_id, impfstelle_id }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    if (response.data.type === 'text/html') {
                        response.data.text().then((text: string) => {
                            const data = JSON.parse(text);
                            errorSwal('Fehler', data.body);
                        });
                    } else {
                        successSwal('ELGA', 'gesucht');
                        dispatch('loadImpfungen');

                        const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                        saveAs(response.data, `ElgaSuche_${date}.xlsx`);
                    }
                })
                .catch((error: AxiosError<{ body?: any }>) => {
                    console.error(error);
                    errorSwal('Fehler', error.response?.data?.body ?? error.message);
                })
                .finally(() => commit('setLoaders', { suche: false }));
        },
        pruefenPatienten({ commit, dispatch, rootState }: Context, payload: PruefenImpfungenPayload): void {
            commit('setLoaders', { verfication: true });

            const arbeitsplatz_id = rootState.session.arbeitsplatz;
            const { impfstelle_id } = rootState.authentication;

            api.post('ivs/impfpass/verifyall', { ...payload, arbeitsplatz_id, impfstelle_id }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    if (response.data.type === 'text/html') {
                        response.data.text().then((text: string) => {
                            const data = JSON.parse(text);
                            errorSwal('Fehler', data.body);
                        });
                    } else {
                        successSwal('ELGA', 'geprüft');
                        dispatch('loadImpfungen');

                        const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                        saveAs(response.data, `ElgaPrüfung_${date}.xlsx`);
                    }
                })
                .catch((error: AxiosError<{ body?: any }>) => {
                    console.error(error);
                    errorSwal('Fehler', error.response?.data?.body ?? error.message);
                })
                .finally(() => commit('setLoaders', { verfication: false }));
        },
        exportImpfungen({ commit, state, rootState }: Context): void {
            commit('setLoaders', { export: true });

            const filter = state.prevFilter;

            api.post('verwaltung/elgauebertragung/export', { filter }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    const date = moment().format('DD-MM-YYYY_HH-mm-ss');

                    const impfstelle = rootState.authentication.impfstelle;

                    const impfstelleAbkuerzung = impfstelle?.abkuerzung
                        ? impfstelle.abkuerzung.replace(/ /g, '_') + '_' : '';

                    saveAs(response.data, `ElgaUebertragung_${impfstelleAbkuerzung}${date}.xlsx`);
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { export: false }));
        },
    },
    mutations: {
        setFilter(state: ElgaUebertragungState, filter: ElgaUebertagungFilter): void {
            state.filter = filter;
        },
        setPrevFilter(state: ElgaUebertragungState, filter: ElgaUebertagungFilter): void {
            state.prevFilter = { ...filter };
        },
        setItems(state: ElgaUebertragungState, payload: Partial<ElgauebertragungEintrag>[]): void {
            state.items = payload.map((data) => new ElgauebertragungEintrag(data));
            state.patienten = _.uniqBy(state.items, 'patnr');

            if (state.selectedItems.length > 0) {
                state.selectedItems = state.patienten.filter(({ patnr }: ElgauebertragungEintrag) => {
                    return state.selectedItems.some((item: ElgauebertragungEintrag) => item.patnr === patnr);
                });
            }
        },
        setLoaders(state: ElgaUebertragungState, loaders: SetElgaUebertragungLoaders): void {
            state.loaders = {
                ...state.loaders,
                ...loaders,
            };
        },
        setPage(state: ElgaUebertragungState, page: number): void {
            state.page = page;
        },
        setSelectedItems(state: ElgaUebertragungState, items: ElgauebertragungEintrag[]): void {
            state.selectedItems = items;
        },
    },
    getters: {
        isUebertragen(state: ElgaUebertragungState): boolean {
            return state.prevFilter.status === UebertragungStatus.NichtUebertragen;
        },
        isStornieren(state: ElgaUebertragungState): boolean {
            return state.prevFilter.status === UebertragungStatus.Uebertragen;
        },
    },
};
